import React from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';

import fastdom from 'fastdom';

import Option from '@ott/option';

import NOOP from 'lodash/noop';
import $get from 'lodash/get';
import $isEqual from 'lodash/isEqual';

class ControlWithOverlay extends React.PureComponent {
  constructor(props, ctx) {
    super(props, ctx);

    this.handleOptionMouseOver = this.handleOptionMouseOver.bind(this);
    this.handleOptionMouseOut = this.handleOptionMouseOut.bind(this);
    this.handleGroupClick = this.handleGroupClick.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleOptionFocus = this.handleOptionFocus.bind(this);
    this.handleOptionBlur = this.handleOptionBlur.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleTab = this.handleTab.bind(this);
    this.handleEnter = this.handleEnter.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.getTriggerValue = this.getTriggerValue.bind(this);
    this.getOptionRenderer = this.getOptionRenderer.bind(this);

    this.state = {
      isOpen: false,
      optionFocused: false,
    };
    this._initial = true;
    this.state.activeOption = this.findActiveOption(props);
  }

  componentWillReceiveProps(nextProps) {
    //вычислять активный элемент нужно только при смене списка элементов
    if (this.props.items !== nextProps.items) {
      this.setState({
        activeOption: this.findActiveOption(nextProps),
      });
    }
  }

  componentDidUpdate() {
    if (this.activeOptionRef && this.state.isOpen && this._activeOptionReveal) {
      this._revealActiveOption();
    }
    if (this.state.isOpen && !this._boundOutside) {
      this.bindOutsideClicks();
    }
    if (!this.state.isOpen && this._boundOutside) {
      this.unbindOutsideClicks();
    }
  }

  componentWillUnmount() {
    fastdom.clear();
    if (this._boundOutside) {
      this.unbindOutsideClicks();
    }
  }

  getOptionRenderer() {
    return this.props.renderOption(...arguments);
  }

  getTriggerValue() {
    const getValue = this.props.renderTriggerValue || this.getOptionRenderer;
    return getValue(...arguments, this.props);
  }

  getSelectHandler() {
    return this.props.onOptionSelect;
  }

  getKey(item, key) {
    if (!item) {
      return item;
    }
    const pathKey = typeof key === 'function' ? key(item, this.props) : key;
    return $get(item, pathKey);
  }

  getActiveItem() {
    return this.active;
  }

  getSelectedItem() {
    return this.selected;
  }

  getHoveredItem() {
    return this.hovered;
  }

  /**
   * @deprecated since version 1.1.12 (use getActiveItem() instead)
   */
  get active() {
    return this.state.activeOption;
  }

  /**
   * @deprecated since version 1.1.12 (use getSelectedItem() instead)
   */
  get selected() {
    return this.state.selected;
  }

  /**
   * @deprecated since version 1.1.12 (use getSelectedHovered() instead)
   */
  get hovered() {
    return this.state.hovered;
  }

  getOption(item, i, el) {
    const { withArrow, withPadding, isTouchDevice } = this.props;

    /*
     * если ты дебажишь лаги - проверь в прод сборке сначала
     * в деве могут быть лаги на большом кол-ве детей, т.к. реакт вызывает проверку пропсов
     * в проде все должно быть ок
     * */
    const { activeOption, selected, hovered } = this.state;
    const isActive = activeOption == item; // eslint-disable-line eqeqeq
    const isSelected = selected == item; // eslint-disable-line eqeqeq
    const isHovered = hovered == item; // eslint-disable-line eqeqeq
    const option = (
      <Option
        key={this.getOptionKey(item, i)}
        ref={ref => (this.activeOptionRef = isActive ? ref : null)}
        pureChildren={this.props.pureChildren}
        isSelected={isSelected}
        isActive={isActive}
        isHovered={isHovered}
        withArrow={withArrow}
        withPadding={withPadding}
        isTouchDevice={isTouchDevice}
        onMouseDown={this.handleOptionFocus}
        onMouseOver={this.handleOptionMouseOver}
        onMouseOut={this.handleOptionMouseOut}
        value={item}
        onClick={this.handleSelect}
        onBlur={this.handleOptionBlur}
        onFocus={this.handleOptionFocus}
      >
        {el}
      </Option>
    );

    const isGroup = this.getKey(item, this.props.groupStarterKey);

    if (isGroup) {
      return [
        <Option
          key="group"
          isGroup
          onTouchStart={this.handleGroupClick}
          onMouseDown={this.handleGroupClick}
        >
          {this.props.renderGroupTitle(item, i, this.props, this.state, this)}
        </Option>,
        <div key="option">{option}</div>,
      ];
    } else {
      return option;
    }
  }

  handleOptionMouseOver(item) {
    clearTimeout(this._mouseOutTO);
    this.setState({
      hovered: item,
    });
  }

  handleOptionMouseOut(item) {
    this._mouseOutTO = setTimeout(
      () =>
        this.setState({
          hovered: null,
        }),
      0
    );
  }

  getOptionKey(item, i) {
    if (this.props.getOptionKey) {
      return this.props.getOptionKey(item, i, this.props);
    }

    let key = this.getKey(item, this.props.valueKey);
    if (typeof key !== 'string') {
      key = i;
    }
    return key;
  }

  getOptions() {
    const { items, autoSelectIfOne } = this.props;
    const renderOption = this.getOptionRenderer.bind(this);

    if (items && autoSelectIfOne && items.length === 1) {
      return null;
    }
    return items && items.map
      ? items.map((item, i) => {
          return this.getOption(item, i, renderOption(item, i, this.props, this.state, this));
        })
      : null;
  }

  getTriggerProps() {
    const { styles, ...restProps } = this.props;

    return {
      ...restProps,
      onFocus: this.handleFocus,
      onClick: this.handleClick,
      onBlur: this.handleBlur,
      onKeyDown: this.handleKeyDown,
      onMouseDown: this.handleMouseDown,
      children: this.props.label,
      isOpen: this.state.isOpen,
    };
  }

  bindOutsideClicks() {
    this._boundOutside = true;
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('touchend', this.handleClickOutside);
  }

  unbindOutsideClicks() {
    if (this._boundOutside) {
      this._boundOutside = false;
      document.removeEventListener('click', this.handleClickOutside);
      document.removeEventListener('touchend', this.handleClickOutside);
    }
  }

  handleGroupClick() {
    this.groupClicked = true;
  }

  handleSelect(item, cb) {
    const shouldCallHandler = item !== this.state.selected;

    this._initial = shouldCallHandler;
    this.setState(
      {
        optionFocused: true,
        selected: item,
      },
      function() {
        this.hideSelect(() => {
          if (cb && typeof cb === 'function') {
            cb(item);
          }

          if (shouldCallHandler) {
            this.getSelectHandler()(item, this.props);
          }
        });
      }
    );
  }

  handleClick(e) {
    if (e) {
      e.preventDefault();
    }
  }

  handleFocus() {
    if (this._forcedFocus || this._ignoreFocus) {
      this._forcedFocus = false;
      this._ignoreFocus = false;
      return;
    }

    if (this._hideSelectTO) {
      clearTimeout(this._hideSelectTO);
    }

    this.setState({
      triggerFocused: true,
      isOpen: true,
    });

    if (this.props.onFocus) {
      this.props.onFocus(arguments);
    }
  }

  handleToggle() {
    if (this._hideSelectTO) {
      clearTimeout(this._hideSelectTO);
    }

    if (!this.state.isOpen) {
      this.setState({
        isOpen: true,
      });
    } else {
      this.hideSelect();
    }
  }

  handleOptionFocus(e) {
    e && e.preventDefault();
    e && e.stopPropagation();

    this.setState({
      optionFocused: true,
    });
  }

  handleOptionBlur() {
    this.setState(
      {
        optionFocused: false,
      },
      function() {
        if (this.state.triggerFocused) {
          return;
        }
        this.handleBlur();
      }
    );
  }

  handleBlur(e) {
    if (this.groupClicked) {
      this.groupClicked = false;
      return;
    }

    if (e && typeof e.persist === 'function') {
      e.persist();
    }

    this.setState(
      {
        triggerFocused: false,
      },
      () => {
        if (this.state.optionFocused) {
          return;
        }

        this.hideOnOutsideNodeClick(e);

        if (this.props.onBlur) {
          this.props.onBlur(e);
        }
      }
    );
  }

  hideSelect(cb) {
    if (this._hideSelectTO) {
      clearTimeout(this._hideSelectTO);
    }

    this.setState(
      {
        isOpen: false,
        hovered: null,
        activeOption: undefined,
        optionFocused: false,
      },
      () => cb && cb()
    );
  }

  checkValues(item, props) {
    const { selected } = this.state;

    if ($isEqual(item, selected)) {
      return item;
    }

    const itemValue = $get(item, props.valueKey, '').toString();
    const value = $get(selected, props.valueKey, '').toString();
    const hasValues = itemValue && value;

    return (
      hasValues && (itemValue.toLowerCase() === value.toLowerCase() || itemValue.startsWith(value))
    );
  }

  findActiveOption(props = this.props) {
    if ($get(props.items, 'length') && props.items.indexOf(this.state.activeOption) === -1) {
      const activeOptionIdx =
        props.initialValue === undefined
          ? undefined
          : Math.max(0, props.items.findIndex(item => this.checkValues(item, props)));

      return props.items[activeOptionIdx];
    }
  }

  _updateIndex(direction) {
    if (!$get(this, 'props.items.length')) {
      return;
    }

    this._activeOptionReveal = true;
    const CURRENT = this.state.activeOption;
    const CURRENT_SELECTED = this.state.selected;
    const CURRENT_IDX = this.props.items.indexOf(CURRENT);
    const LAST_IDX = this.props.items.length - 1;
    const { skipActive } = this.props;

    const updateDirection = currentIdx => {
      let nextIdx;
      if (direction === 'down') {
        nextIdx = this.updateIndexDown(currentIdx, LAST_IDX);
      } else {
        nextIdx = this.updateIndexUp(currentIdx, LAST_IDX);
      }
      const nextItem = this.props.items[nextIdx];
      if (!skipActive && nextItem === CURRENT_SELECTED) {
        return updateDirection(nextIdx);
      }
      return nextItem;
    };
    const nextItem = updateDirection(CURRENT_IDX);
    const activeOption = nextItem != null ? nextItem : undefined;
    this.setState({ activeOption });
  }

  handleTab(e) {
    if (e.shiftKey) {
      return true;
    }
    if (this.state.isOpen) {
      e.preventDefault();
      this._updateIndex('down');
    }
  }

  handleEnter(e) {
    const { activeOption } = this.state;

    if (activeOption) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
          e.nativeEvent.stopImmediatePropagation();
        }
      }

      this.handleSelect(activeOption);
    } else {
      this.handleToggle();
    }
  }

  handleMouseDown(e) {
    if (this.state.optionFocused) {
      this.handleBlur();
    } else {
      this.handleFocus();
    }
  }

  handleKeyDown(e) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!this.state.isOpen) {
          this.handleEnter(e);
        }
        this._updateIndex('down');
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!this.state.isOpen) {
          this.handleEnter(e);
        }
        this._updateIndex('up');
        break;
      case 'Enter':
      case ' ':
        this.handleEnter(e);
        break;
      case 'Escape':
        this.hideSelect(this.handleBlur);
        break;
      case 'Tab':
        this.handleTab(e);
        break;
      default:
      // do nothing
    }
  }

  handleClickOutside(e) {
    this.hideOnOutsideNodeClick(e);
  }

  hideOnOutsideNodeClick(e) {
    const domNode = findDOMNode(this);
    if (!domNode || !domNode.contains(e && e.target)) {
      this.hideSelect();
    }
  }

  updateIndexDown(cur, last) {
    if (cur < 0) {
      return 0;
    }
    if (cur < last) {
      return cur + 1;
    } else if (cur === last) {
      return 0;
    } else {
      return last;
    }
  }

  updateIndexUp(cur, last) {
    if (cur <= 0) {
      return last;
    }
    if (cur <= last) {
      return cur - 1;
    } else {
      return last;
    }
  }

  _revealActiveOption() {
    fastdom.clear();
    fastdom.measure(() => {
      if (!this.activeOptionRef) {
        return;
      }

      const activeDOM = findDOMNode(this.activeOptionRef);
      const wrapDOM = findDOMNode(this.optionsWrapRef || this);
      const activeRect = activeDOM.getBoundingClientRect();
      const wrapRect = wrapDOM.getBoundingClientRect();

      if (activeRect.bottom > wrapRect.bottom || activeRect.top < wrapRect.top) {
        const toScroll = activeDOM.offsetTop + activeDOM.offsetHeight - wrapDOM.offsetHeight;
        fastdom.mutate(() => {
          wrapDOM.scrollTop = toScroll;
        });
      }
    });

    this._activeOptionReveal = false;
  }
}

ControlWithOverlay.propTypes = Object.assign({
  name: PropTypes.string,
  defaultChecked: PropTypes.bool,
  defaultValue: PropTypes.any,
  value: PropTypes.any,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onDragStart: PropTypes.func,
  onDrop: PropTypes.func,
  onFocus: PropTypes.func,
  onUpdate: PropTypes.func,
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
  checked: PropTypes.bool,
  dirty: PropTypes.bool,
  pristine: PropTypes.bool,
  error: PropTypes.any,
  active: PropTypes.bool,
  touched: PropTypes.bool,
  visited: PropTypes.bool,
  onOptionSelect: PropTypes.func,
  renderOption: PropTypes.func,
  renderTriggerValue: PropTypes.func,
  getOptionKey: PropTypes.func,
  skipActive: PropTypes.bool,
  valueKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  groupStarterKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  items: PropTypes.array.isRequired,
  isTouchDevice: PropTypes.bool.isRequired,
});

ControlWithOverlay.defaultProps = {
  onChange: NOOP,
  onBlur: NOOP,
  renderOption: (v, i) => <span key={i}>{v}</span>,
  renderGroup: () => null,
  skipActive: false,
  groupStarterKey: '$groupStarter',
};

export default ControlWithOverlay;
